import { useMutation } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { Dispatch, useCallback, useEffect, useState } from 'react'
import { useTRPC } from '~/services/trpc'

import { LOAD_QUERY } from '../constants'
import {
  LoadQueryAction,
  SavedQueryAction,
  SavedQueryState,
  SavedQueryType,
} from '../types'

export function useSavedQueryInitializer(
  qid: string,
  initialQuery: SavedQueryState['data']['params'],
  dispatch: Dispatch<SavedQueryAction>,
  type?: SavedQueryType,
) {
  const trpc = useTRPC()
  const [ready, setReadyState] = useState(false)
  const mutator = useMutation(trpc.queries.findOrCreate.mutationOptions())
  const search = useSearch({ from: '/_authenticated/$alias/_app' })

  const parseInitialParams = useCallback(() => {
    let state
    const initialParams = {
      rows: [...initialQuery.rows],
      columns: [...initialQuery.columns],
      aggregations: [...initialQuery.aggregations],
      timespan: { ...initialQuery.relativeTimespan },
      fields: [...initialQuery.fields],
      filters: [...initialQuery.filters],
    }
    if (search.ieq) state = JSON.parse(atob(search.ieq))
    if (state && state.query?.timespan) initialParams.timespan = state.query.timespan
    if (state && state.query?.filters) {
      for (const filter of state.query.filters) {
        if (
          filter?.values?.length ||
          filter?.value ||
          ['isNotNull', 'isNull'].includes(filter?.op)
        )
          initialParams.filters.push(filter)
      }
    }
    return initialParams
  }, [search])

  useEffect(() => {
    mutator
      .mutateAsync({
        id: qid,
        type: type,
        params: parseInitialParams(),
      })
      .then((data) => {
        if (data?.params && Object.keys(data.params).length) {
          dispatch({
            type: LOAD_QUERY,
            payload: {
              name: data.name,
              description: data.description,
              shared: data.shared,
              saved: data.saved,
              params: data.params,
              createdBy: data.createdBy,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
              updatedBy: data.updatedBy,
              task: data.task,
            } as unknown as LoadQueryAction['payload'],
          })
          setReadyState(true)
        }
      })
      .catch(console.error)
  }, [qid])

  return [ready] as const
}
